import { GlButton, GlCollapsibleListbox, GlModalDirective } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { produce } from 'immer';
import { differenceBy, debounce } from 'lodash-es';
import BoardForm from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/boards/components/board_form.vue?vue3';
import { formType } from '~/boards/constants';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { isModifierKey } from '~/lib/utils/common_utils';
import { visitUrl } from '~/lib/utils/url_utility';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { s__, __ } from '~/locale';
import groupBoardsQuery from '../graphql/group_boards.query.graphql';
import projectBoardsQuery from '../graphql/project_boards.query.graphql';
import groupRecentBoardsQuery from '../graphql/group_recent_boards.query.graphql';
import projectRecentBoardsQuery from '../graphql/project_recent_boards.query.graphql';
import { setError } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/boards/graphql/cache_updates.js?vue3';
import { fullBoardId } from '../boards_util';
const MIN_BOARDS_TO_VIEW_RECENT = 10;
export default {
    name: 'BoardsSelector',
    i18n: {
        fetchBoardsError: s__('Boards|An error occurred while fetching boards. Please try again.'),
        headerText: s__('Boards|Switch board'),
        noResultsText: s__('Boards|No matching boards found'),
        hiddenBoardsText: s__('Boards|Some of your boards are hidden, add a license to see them again.')
    },
    components: {
        BoardForm,
        GlButton,
        GlCollapsibleListbox
    },
    directives: {
        GlModalDirective
    },
    inject: [
        'boardBaseUrl',
        'fullPath',
        'canAdminBoard',
        'multipleIssueBoardsAvailable',
        'hasMissingBoards',
        'scopedIssueBoardFeatureEnabled',
        'weights',
        'boardType',
        'isGroupBoard'
    ],
    props: {
        board: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        isCurrentBoardLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        boardModalForm: {
            type: String,
            required: false,
            default: ''
        }
    },
    emits: [
        'show-board-modal',
        'switch-board',
        'update-board'
    ],
    data () {
        return {
            boards: [],
            recentBoards: [],
            loadingBoards: false,
            loadingRecentBoards: false,
            filterTerm: ''
        };
    },
    computed: {
        boardName () {
            return this.board?.name || s__('Boards|Select board');
        },
        boardId () {
            return getIdFromGraphQLId(this.board.id) || '';
        },
        parentType () {
            return this.boardType;
        },
        issueBoardsQuery () {
            return this.isGroupBoard ? groupBoardsQuery : projectBoardsQuery;
        },
        boardsQuery () {
            return this.issueBoardsQuery;
        },
        loading () {
            return this.loadingRecentBoards || this.loadingBoards;
        },
        listBoxItems () {
            const mapItems = ({ id, name })=>({
                    text: name,
                    value: id
                });
            if (this.showRecentSection) {
                const notRecent = differenceBy(this.filteredBoards, this.recentBoards, 'id');
                return [
                    {
                        text: __('Recent'),
                        options: this.recentBoards.map(mapItems)
                    },
                    {
                        text: __('All'),
                        options: notRecent.map(mapItems)
                    }
                ];
            }
            return this.filteredBoards.map(mapItems);
        },
        filteredBoards () {
            return this.boards.filter((board)=>board.name.toLowerCase().includes(this.filterTerm.toLowerCase()));
        },
        showCreate () {
            return this.multipleIssueBoardsAvailable;
        },
        isLastBoard () {
            return this.boards.length === 1;
        },
        showDropdown () {
            return this.showCreate || this.hasMissingBoards;
        },
        showRecentSection () {
            return this.recentBoards.length > 0 && this.boards.length > MIN_BOARDS_TO_VIEW_RECENT && !this.filterTerm.length;
        }
    },
    watch: {
        board (newBoard) {
            this.updateDocumentTitle(newBoard);
        }
    },
    created () {
        this.handleSearch = debounce(this.setFilterTerm, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
    },
    destroyed () {
        this.handleSearch.cancel();
    },
    methods: {
        updateDocumentTitle (newBoard) {
            if (!newBoard?.name) {
                return;
            }
            // The server renders the title as "Issue Boards · [board name] · Boards · [Project] · GitLab".
            // Replace only the board-name segment so the prefix and project/instance suffix are preserved,
            // instead of clobbering the whole title with the bare board name.
            const separator = ' · ';
            const segments = document.title.split(separator);
            const boardNameIndex = 1;
            if (segments.length > boardNameIndex) {
                segments[boardNameIndex] = newBoard.name;
                document.title = segments.join(separator);
            } else {
                document.title = newBoard.name;
            }
        },
        fullBoardId (boardId) {
            return fullBoardId(boardId);
        },
        cancel () {
            this.$emit('show-board-modal', '');
        },
        boardUpdate (data, boardType) {
            if (!data?.[this.parentType]) {
                return [];
            }
            return data[this.parentType][boardType].nodes.map((node)=>({
                    id: getIdFromGraphQLId(node.id),
                    name: node.name
                }));
        },
        recentBoardsQuery () {
            return this.isGroupBoard ? groupRecentBoardsQuery : projectRecentBoardsQuery;
        },
        loadBoards (toggleDropdown = true) {
            if (toggleDropdown && this.boards.length > 0) {
                return;
            }
            this.$apollo.addSmartQuery('boards', {
                variables () {
                    return {
                        fullPath: this.fullPath
                    };
                },
                query: this.boardsQuery,
                update: (data)=>this.boardUpdate(data, 'boards'),
                watchLoading: (isLoading)=>{
                    this.loadingBoards = isLoading;
                },
                error (error) {
                    setError({
                        error,
                        message: this.$options.i18n.fetchBoardsError
                    });
                }
            });
            this.loadRecentBoards();
        },
        loadRecentBoards () {
            this.$apollo.addSmartQuery('recentBoards', {
                variables () {
                    return {
                        fullPath: this.fullPath
                    };
                },
                query: this.recentBoardsQuery,
                update: (data)=>this.boardUpdate(data, 'recentIssueBoards'),
                watchLoading: (isLoading)=>{
                    this.loadingRecentBoards = isLoading;
                },
                error (error) {
                    setError({
                        error,
                        message: s__('Boards|An error occurred while fetching recent boards. Please try again.')
                    });
                }
            });
        },
        addBoard (board) {
            const { defaultClient: store } = this.$apollo.provider.clients;
            const variables = {
                fullPath: this.fullPath
            };
            const sourceData = store.readQuery({
                query: this.boardsQuery,
                variables
            });
            if (sourceData) {
                const newData = produce(sourceData, (draftState)=>{
                    draftState[this.parentType].boards.nodes = [
                        ...draftState[this.parentType].boards.nodes,
                        {
                            ...board
                        }
                    ];
                });
                store.writeQuery({
                    query: this.boardsQuery,
                    variables,
                    data: newData
                });
            } else {
                this.$apollo.queries.boards.refetch();
            }
            this.$emit('switch-board', board.id);
        },
        setFilterTerm (value) {
            this.filterTerm = value;
        },
        async switchBoardKeyEvent (boardId, e) {
            if (isModifierKey(e)) {
                e.stopPropagation();
                visitUrl(`${this.boardBaseUrl}/${boardId}`, true);
            }
        },
        switchBoardGroup (value) {
            // Epic board ID is supported in EE version of this file
            this.$emit('switch-board', this.fullBoardId(value));
        }
    },
    formType
};
